/**
 * @fileoverview Module for `src/lib/unsignedPartners.js`.
 */

import axios from 'axios';
import { getGrants } from './user';

/**
 * Partner agreement helpers for users with pending partner-sign grants.
 */

/**
 * Returns the next signable partner currently stored in localStorage.
 *
 * @returns {{slug: string|null, id: string|null, name: string|null}} Partner data.
 */
function getNextSignablePartner() {
  return {
    slug: localStorage.getItem('next_signable_partner_slug'),
    id: localStorage.getItem('next_signable_partner_id'),
    name: localStorage.getItem('next_signable_partner_name'),
  };
}

/**
 * Signs the currently stored next partner agreement and refreshes local state.
 *
 * @param {Object} auth - Auth API object.
 * @returns {Promise<void>}
 */
async function signNextPartner(auth) {
  const accessToken = await auth.getAccessToken();
  await axios.put(`${ process.env.REACT_APP_API_URL }/v2/users/${ auth.getUserId() }/signedPartnerAgreements`, getNextSignablePartner(), {
    headers: {
      Authorization: 'Bearer '.concat(accessToken),
    },
  });

  await updateNextSignablePartner(auth);
}

/**
 * Updates localStorage with the next partner agreement still needing signature.
 *
 * @param {Object} auth - Auth API object.
 * @param {Object} [options]
 * @param {Function} [options.isCurrent] - Checked after the lookups; when it returns false, the
 *   session the lookups were for has been replaced, so nothing is stored.
 * @returns {Promise<void>}
 */
async function updateNextSignablePartner(auth, { isCurrent } = {}) {
  const accessToken = await auth.getAccessToken();

  const adminPartnerResponses = getGrants().filter((grant) => (
    grant.permission === 'partner.sign' && !(grant.resource.includes('*'))
  )).map((grant) => (
    axios.get(`${ process.env.REACT_APP_API_URL }/v1/partners/${ grant.resource.split(':')[1] }`, {
      headers: {
        Authorization: 'Bearer '.concat(accessToken),
      },
    })
  ));

  /**
   * Finds the first partner from the user's grants that still needs an agreement signature.
   */
  const nextSignablePartner = (await Promise.all(adminPartnerResponses)).find((response) => (
    !(response.data.signedAt || (response.data.agreementOverride && response.data.agreementOverride.length > 0))
  ));

  if (isCurrent && !isCurrent()) {
    return;
  }

  localStorage.setItem('next_signable_partner_slug', nextSignablePartner ? nextSignablePartner.data.slug : '');
  localStorage.setItem('next_signable_partner_id', nextSignablePartner ? nextSignablePartner.data.id : '');
  localStorage.setItem('next_signable_partner_name', nextSignablePartner ? nextSignablePartner.data.name : '');
}

export {
  getNextSignablePartner,
  signNextPartner,
  updateNextSignablePartner,
};
